import { Game } from '../core/Game';
import { MapDefinition } from '../editor/MapTypes';
import { Vector2 } from '../utils/Vector2';

export type DifficultyLevel = 1 | 2 | 3 | 4 | 5 | 6;

interface DifficultyInfo {
  name: string;
  description: string;
  waveBonus: number; // Equivalent waves added for scaling
}

export const DIFFICULTY_LEVELS: Record<DifficultyLevel, DifficultyInfo> = {
  1: {
    name: 'Normal',
    description: 'Standard gameplay. This is how the game was meant to be played. No tricks, no gimmicks, just pure tower defense action.',
    waveBonus: 0,
  },
  2: {
    name: 'Hard',
    description: 'Enemies got the memo that you\'re getting good. They\'re bringing friends. Lots of friends. And they\'re not happy.',
    waveBonus: 8,
  },
  3: {
    name: 'Harder',
    description: 'The enemies have formed a union and demanded better health insurance. Spoiler: they got it. You didn\'t.',
    waveBonus: 16,
  },
  4: {
    name: 'Hardest',
    description: 'Enemies have been training in the hyperbolic time chamber. They\'ve seen every strategy guide. They know your moves.',
    waveBonus: 24,
  },
  5: {
    name: 'Nightmare',
    description: 'The enemies have achieved sentience and are actively plotting against you. They have spreadsheets. They have meetings. They have vendettas.',
    waveBonus: 32,
  },
  6: {
    name: 'Why?',
    description: 'You\'ve crossed a line. The enemies have transcended physical form. They are now concepts. Concepts of pain. Concepts of suffering. You asked for this.',
    waveBonus: 40,
  },
};

interface MapPlaySceneCallbacks {
  onBack?: () => void;
}

export class MapPlayScene {
  private root: HTMLDivElement;
  private game: Game | null = null;
  private selectedDifficulty: DifficultyLevel | null = null;
  private difficultyDialog: HTMLDivElement | null = null;
  private callbacks: MapPlaySceneCallbacks;

  constructor(
    private mapData: MapDefinition,
    callbacks: MapPlaySceneCallbacks = {},
  ) {
    this.callbacks = callbacks;
    this.root = document.createElement('div');
    this.root.className = 'map-play-scene';
    this.root.style.display = 'none';
    this.ensureStyles();
    this.buildLayout();
    document.body.appendChild(this.root);
  }

  show(): void {
    this.root.style.display = 'flex';
    if (!this.selectedDifficulty) {
      this.showDifficultySelection();
    }
  }

  hide(): void {
    this.root.style.display = 'none';
    if (this.game) {
      // Clean up game instance if needed
      this.game = null;
    }
  }

  private ensureStyles(): void {
    if (document.getElementById('map-play-scene-styles')) return;
    const style = document.createElement('style');
    style.id = 'map-play-scene-styles';
    style.textContent = `
      .map-play-scene {
        position: fixed;
        inset: 0;
        background: #050505;
        color: #fff;
        font-family: 'Courier New', monospace;
        z-index: 11000;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .map-play-game-area {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
      }
      .map-play-ui-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 10;
      }
      .map-play-ui-overlay > * {
        pointer-events: auto;
      }
      .map-play-difficulty-dialog {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 12000;
      }
      .map-play-difficulty-panel {
        background: #1a1a1a;
        border: 2px solid #444;
        border-radius: 12px;
        padding: 40px;
        max-width: 600px;
        max-height: 80vh;
        overflow-y: auto;
      }
      .map-play-difficulty-title {
        font-size: 36px;
        font-weight: bold;
        margin-bottom: 20px;
        text-align: center;
      }
      .map-play-difficulty-option {
        background: #252525;
        border: 2px solid #555;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 12px;
        cursor: pointer;
        transition: all 0.2s;
      }
      .map-play-difficulty-option:hover {
        background: #333;
        border-color: #777;
      }
      .map-play-difficulty-option.selected {
        background: #2a4a2a;
        border-color: #4a8a4a;
      }
      .map-play-difficulty-name {
        font-size: 23px;
        font-weight: bold;
        margin-bottom: 6px;
        color: #ffd700;
      }
      .map-play-difficulty-desc {
        font-size: 17px;
        color: #ccc;
        line-height: 1.4;
      }
      .map-play-difficulty-confirm {
        margin-top: 24px;
        text-align: center;
      }
      .map-play-difficulty-confirm-btn {
        background: #00a86b;
        border: none;
        color: #fff;
        padding: 12px 32px;
        font-size: 21px;
        font-family: 'Courier New', monospace;
        border-radius: 6px;
        cursor: pointer;
        font-weight: bold;
      }
      .map-play-difficulty-confirm-btn:hover {
        background: #00c87a;
      }
      .map-play-difficulty-confirm-btn:disabled {
        background: #444;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  private buildLayout(): void {
    const gameArea = document.createElement('div');
    gameArea.className = 'map-play-game-area';
    this.root.appendChild(gameArea);

    const uiOverlay = document.createElement('div');
    uiOverlay.className = 'map-play-ui-overlay';
    this.root.appendChild(uiOverlay);

    // Back button
    const backBtn = document.createElement('button');
    backBtn.textContent = '← Back to Editor';
    backBtn.style.cssText = `
      position: absolute;
      top: 16px;
      left: 16px;
      background: rgba(100, 0, 0, 0.8);
      border: 1px solid #666;
      color: #fff;
      padding: 8px 16px;
      font-family: 'Courier New', monospace;
      font-size: 18px;
      cursor: pointer;
      border-radius: 4px;
    `;
    backBtn.onclick = () => {
      this.hide();
      this.callbacks.onBack?.();
    };
    uiOverlay.appendChild(backBtn);
  }

  private showDifficultySelection(): void {
    if (this.difficultyDialog) return;

    const dialog = document.createElement('div');
    dialog.className = 'map-play-difficulty-dialog';
    this.difficultyDialog = dialog;

    const panel = document.createElement('div');
    panel.className = 'map-play-difficulty-panel';

    const title = document.createElement('div');
    title.className = 'map-play-difficulty-title';
    title.textContent = 'Select Difficulty';
    panel.appendChild(title);

    const desc = document.createElement('div');
    desc.style.cssText = 'text-align: center; margin-bottom: 24px; color: #aaa; font-size: 14px;';
    desc.textContent = `Map: ${this.mapData.name || 'Unnamed'}`;
    panel.appendChild(desc);

    let selected: DifficultyLevel | null = null;

    for (let level = 1; level <= 6; level++) {
      const info = DIFFICULTY_LEVELS[level as DifficultyLevel];
      const option = document.createElement('div');
      option.className = 'map-play-difficulty-option';
      option.onclick = () => {
        selected = level as DifficultyLevel;
        document.querySelectorAll('.map-play-difficulty-option').forEach(el => {
          el.classList.remove('selected');
        });
        option.classList.add('selected');
        confirmBtn.disabled = false;
      };

      const name = document.createElement('div');
      name.className = 'map-play-difficulty-name';
      name.textContent = `${level}. ${info.name}`;
      option.appendChild(name);

      const description = document.createElement('div');
      description.className = 'map-play-difficulty-desc';
      description.textContent = info.description;
      option.appendChild(description);

      panel.appendChild(option);
    }

    const confirmDiv = document.createElement('div');
    confirmDiv.className = 'map-play-difficulty-confirm';
    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'map-play-difficulty-confirm-btn';
    confirmBtn.textContent = 'Start Game';
    confirmBtn.disabled = true;
    confirmBtn.onclick = () => {
      if (selected) {
        this.selectedDifficulty = selected;
        dialog.remove();
        this.difficultyDialog = null;
        this.startGame(selected);
      }
    };
    confirmDiv.appendChild(confirmBtn);
    panel.appendChild(confirmDiv);

    dialog.appendChild(panel);
    this.root.appendChild(dialog);
  }

  private startGame(difficulty: DifficultyLevel): void {
    const container = document.createElement('div');
    container.id = 'map-play-game-container';
    container.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    `;
    const gameArea = this.root.querySelector('.map-play-game-area');
    if (gameArea) {
      gameArea.appendChild(container);
    }

    this.game = new Game(container);
    // Stop any existing music before starting new game to prevent multiple tracks
    const existingAudioSystem = this.game.getAudioSystem();
    if (existingAudioSystem) {
      existingAudioSystem.stopMusic();
    }
    this.game.loadMapData(this.mapData, difficulty);
    this.game.setCanvasVisible(true);
    this.game.startSinglePlayerSession();
  }
}

